import React from 'react';
import { Caption1, Card, CardPreview, Text, Button, Spinner, Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions, Tooltip } from '@fluentui/react-components';
import { ProductImage } from '../Helpers/ProductImage';
import {CartRegular, NewFilled, ProhibitedRegular, PlayCircleFilled, BuildingFactoryRegular, TagRegular, DismissRegular} from '@fluentui/react-icons';
import useStyles from '../../styles';
import { useNavigate } from 'react-router-dom';
import { IProduct, ISearchContext } from '../../Api/Models/Products';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import TruncateText from '../Helpers/TruncateText';
import {FALLBACK_IMAGE} from "../../Constants/FallbackImage";
import {useAddBundleToCartMutation, useAddSavedSearchToCartMutation, useAddToCartMutation, useGetStorefrontQuery} from "../../Api/Api";
import {useSelector} from "react-redux";
import {RootState} from "../../Store/Store";
import {useStartNewCart} from "../Cart/useStartNewCart";
import {IActionResponse} from "../../Api/Models/ActionResponse";
import {
    useAddBundleToPreparedCartMutation,
    useAddToPreparedCartMutation,
    useGetPreparedCartQuery
} from "../../Api/PreparedCartEndpoints";
import CAN, {CART_MANAGEMENT, PRICING} from "../Auth/Permissions";
import ProductVideoModal from "../Product/ProductVideoModal";
import {importantStyleRef, FEATURED_TAG} from "../Helpers/searchPageConfig";
import {tagColor} from "../Helpers/ColorHelpers";
import {useLocalization} from "../Helpers/useLocalization";
import {CART_DRAG_MIME} from "../Cart/useAddToCartAction";
import FavoriteButton from "../Product/FavoriteButton";
import QuotePrice from "../Product/QuotePrice";
import PriceQuantityNotice from "../Product/PriceQuantityNotice";
import PromoPrice from "../Product/PromoPrice";
import StockAvailabilityNotice from "../Product/StockAvailabilityNotice";

interface IProductCardProps {
    product: IProduct;
    showAddToCart?: boolean;
    isDark?: boolean;
    showFeaturedFlag?: boolean;
    // Optional presentation overrides (used by the Search page "cards" view so that
    // the searchPageConfig card/title/price styling applies here too).
    cardStyle?: React.CSSProperties;
    // The card's configured hover look (homePageConfig.cardHover / searchPageConfig.cardHover).
    // Applied INLINE on hover rather than left to the scoped `.card:hover` stylesheet rule, because
    // cardStyle is applied inline-!important (see importantStyleRef below) and an inline !important
    // outranks a stylesheet !important — so the :hover rule could never repaint any property the
    // resting style had already set. A merchant's amber hover glow was silently dead on every card
    // whose resting style set a boxShadow; only `transform`, absent from the resting style, survived.
    cardHoverStyle?: React.CSSProperties;
    nameStyle?: React.CSSProperties;
    priceStyle?: React.CSSProperties;
    imageStyle?: React.CSSProperties;
    // searchPageConfig.addToCartButton — merged into the floating add-to-cart Button.
    addToCartStyle?: React.CSSProperties;
    // Optional top-edge filter shortcuts (Search page "cards" view). When provided and
    // the product carries the matching data, a small icon appears on the card's top edge
    // that applies that manufacturer / category as a search filter.
    onFilterManufacturer?: () => void;
    onFilterCategory?: () => void;
    // Search "cards" view: clicking an attribute chip toggles that facet filter. When provided, the
    // chips become interactive; activeAttributes drives their selected styling. The product's category is
    // passed so the handler can adopt it as the active category when nothing else scopes the filter.
    onFilterAttribute?: (key: string, value: string, category?: string) => void;
    activeAttributes?: { name: string; value: string }[];
    // Search "cards" view: clicking a tag decoration toggles that tag filter. When provided, the
    // tag chips become interactive; activeTags drives their selected styling.
    onFilterTag?: (value: string) => void;
    activeTags?: string[];
    // "Recommended For You" row: when provided, a dismiss (✕) control appears on the card that
    // removes this product from the row and stops it from being recommended again.
    onDismiss?: (productId: string) => void;
    // Interest-signal context: the surface this card is rendered on (search/section/product/…) and,
    // when the surface is search, the active search criteria. Forwarded to the favorite toggle, the
    // add-to-cart call, and (via router state) the product page's view query, so the BFF can capture
    // them onto the interest signal.
    source?: string;
    searchContext?: ISearchContext;
}

// The left inset of the price TEXT within its own box.
//
// A merchant commonly styles the price as a pill (padding + background + border), which insets the
// digits from the card's left edge. The struck original price below carries no pill, so aligning the
// two boxes leaves the two NUMBERS out of line by exactly that inset — visible as the "was" sitting
// slightly left of the price it replaced. Mirroring the inset lines the digits up.
//
// Reads the configured style rather than hard-coding a value, because the pill is merchant config and
// any constant here would be wrong for every storefront but one. Only simple, self-contained values
// are handled: anything exotic falls back to no inset, which is today's behavior.
const priceTextInset = (priceStyle?: React.CSSProperties): string | number | undefined => {
    if (!priceStyle) return undefined;
    const pad = priceStyle.paddingLeft ?? (typeof priceStyle.padding === 'string'
        // CSS shorthand: 1 value = all sides, 2 = vertical/horizontal, 3 = top/horizontal/bottom,
        // 4 = top/right/bottom/left.
        ? (() => {
            const parts = priceStyle.padding.trim().split(/\s+/);
            return parts.length === 1 ? parts[0] : parts.length === 4 ? parts[3] : parts[1];
        })()
        : undefined);
    const border = priceStyle.borderLeftWidth
        ?? (typeof priceStyle.borderWidth === 'string' ? priceStyle.borderWidth.trim().split(/\s+/)[0] : undefined);
    // Both in px, or just the padding. Mixed/relative units are left to the padding alone rather than
    // emitting a calc() that could be nonsense.
    const px = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' && v.endsWith('px') ? parseFloat(v) : undefined);
    const p = px(pad), b = px(border);
    if (p !== undefined && b !== undefined) return p + b;
    return pad ?? undefined;
};

const ProductCard: React.FC<IProductCardProps> = (props) => {
    const cartAddPermission = CAN(CART_MANAGEMENT);
    const canSeePricing = CAN(PRICING);
    const settings = useSelector((state: RootState) => state.cart);
    const { startNewCart, canStartNewCart } = useStartNewCart();
    const styles = useStyles();
    const L = useLocalization();
    const nav = useNavigate();
    // Captured out of props so the click handler closes over a narrowed value instead of needing a
    // non-null assertion. Both top-left controls are computed once here since their placement
    // depends on each other.
    const onDismiss = props.onDismiss;
    const showsManufacturerFilter = !!props.onFilterManufacturer && !!props.product.manufacturer;
    const dismissLabel = L('localization_dismiss_recommendation');
    const [addBundleToCart, { isLoading: isBundleLoading }] = useAddBundleToCartMutation();
    const [addBundleToPreparedCart, { isLoading: isPreparedCartBundleLoading }] = useAddBundleToPreparedCartMutation();
    const [addToCart, { isLoading: isProductLoading }] = useAddToCartMutation();
    const [addToPreparedCart, { isLoading: isPreparedCartProductLoading }] = useAddToPreparedCartMutation();
    const [addSavedToCart, { isLoading: isSavedLoading }] = useAddSavedSearchToCartMutation();
    const [added, setAdded] = React.useState(false);
    const [blockedDialogOpen, setBlockedDialogOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const isLoading = isBundleLoading || isProductLoading || isSavedLoading || isPreparedCartBundleLoading || isPreparedCartProductLoading;
    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});

    // Optional hover image swap (storefront-level setting). The hover image is the
    // first additional image distinct from the primary thumbnail.
    const sfSettings = useGetStorefrontQuery();
    const primaryImage = props.product.image && props.product.image !== '' ? props.product.image : FALLBACK_IMAGE;
    const hoverImage = (sfSettings.data?.enableImageHoverSwap ?? false)
        ? (props.product.images ?? []).find(i => !!i && i !== props.product.image && i !== primaryImage)
        : undefined;

    // Optional video playback (storefront-level setting). Show a play button on the
    // image when the storefront allows it and the product has at least one video URL.
    const [videoOpen, setVideoOpen] = React.useState(false);
    // Only meaningful when cardHoverStyle is supplied; the handlers aren't wired otherwise.
    const [cardHovered, setCardHovered] = React.useState(false);
    const videoUrl = (sfSettings.data?.allowProductVideoPlayback ?? false)
        ? (props.product.videoUrls ?? []).find(v => !!v && v.trim() !== '')
        : undefined;

    // Optional product tags (storefront-level setting). Shown as colorful chips overlaid on
    // the image; clickable to filter when onFilterTag is provided (Search "cards" view).
    const productTags = (sfSettings.data?.enableProductTags ?? false) ? (props.product.tags ?? []).filter(t => !!t && t.trim() !== '') : [];
    // "Featured" is a per-storefront flag rendered as a normal tag chip (replacing the old blocky
    // FEATURED badge). It leads the chip list and is clickable/searchable like any other tag.
    const showFeatured = (props.showFeaturedFlag !== false) && !!props.product.featured;
    const tags = showFeatured ? [FEATURED_TAG, ...productTags.filter(t => t !== FEATURED_TAG)] : productTags;

    // Derive directly — no intermediate state, no render lag
    const hasPreparedCart = !!settings.selectedCartId && !!getPreparedCart.data;
    const preparedCartAllowsAdd = hasPreparedCart && (getPreparedCart.data?.canAddAdditionalItems ?? false);
    const canAddToCart = (props.showAddToCart ?? false) && (!hasPreparedCart || preparedCartAllowsAdd) && cartAddPermission;
    const cartBlocked = (props.showAddToCart ?? false) && hasPreparedCart && !preparedCartAllowsAdd;

    const showError = (msg: string) => {
        setErrorMessage(msg);
        setTimeout(() => setErrorMessage(''), 5000);
    };

    const checkMessages = (cart?: { messages?: string[] }): boolean => {
        if (cart?.messages && cart.messages.length > 0) {
            showError(cart.messages.join(' '));
            return true;
        }
        return false;
    };

    const checkError = (result: { error?: unknown }): boolean => {
        if (result.error) {
            const actionResponse = (result.error as any)?.data as IActionResponse | undefined;
            showError(actionResponse?.error ?? actionResponse?.message ?? 'An error occurred. Please try again.');
            return true;
        }
        return false;
    };

    const click = () => {
        // Carry the origin surface (+ search criteria, when relevant) as router state so the
        // destination page's product-view query can pass them along to the BFF.
        const navOptions = (props.source || props.searchContext)
            ? { state: { source: props.source, searchContext: props.searchContext } }
            : undefined;
        if(props.product.savedSearchId) {
            nav(`/dynamicProducts/${props.product.savedSearchId}`, navOptions);
        }
        else if(props.product.bundleId){
            nav(`/bundles/${props.product.bundleId}`, navOptions)
        }
        else{
            nav(`/products/${props.product.id}`, navOptions);
        }
    }

    // Deepest category level the product carries — used for the top-edge category filter.
    const categoryLabel = props.product.subCategory || props.product.category || props.product.segment;

    // The image area (CardPreview). Its background historically tints to match the card
    // background; a configured `image` style augments/overrides that. The theme forces
    // `.card .fui-CardPreview { background !important }`, so apply via importantStyleRef.
    const imageAreaStyle: React.CSSProperties = {
        ...(props.cardStyle?.backgroundColor ? { backgroundColor: props.cardStyle.backgroundColor } : {}),
        ...(props.imageStyle ?? {}),
        // When the image area has its own styling, clip the image to it so the border stays
        // visible (the image no longer overhangs it) and the radius rounds the image corners.
        ...(props.imageStyle && Object.keys(props.imageStyle).length ? { overflow: 'hidden' } : {}),
    };

    // Resting style, with the configured hover merged over it while the pointer is on the card.
    // Un-hovering must CLEAR the properties that only the hover set (a lone `transform`, say):
    // importantStyleRef only ever writes properties, so anything it wrote once would otherwise stick.
    // Setting them to '' removes the declaration, which is what restores the resting look.
    const cardStyleWithHover = React.useMemo(() => {
        if (!props.cardStyle && !props.cardHoverStyle) return undefined;
        if (cardHovered) return { ...props.cardStyle, ...props.cardHoverStyle };
        const hoverOnlyCleared = Object.fromEntries(
            Object.keys(props.cardHoverStyle ?? {})
                .filter((k) => !(props.cardStyle && k in props.cardStyle))
                .map((k) => [k, '']),
        );
        return { ...hoverOnlyCleared, ...props.cardStyle };
    }, [props.cardStyle, props.cardHoverStyle, cardHovered]);

    return (
        <Card
            className={`card${props.isDark === true ? ' card-force-dark' : props.isDark === false ? ' card-force-light' : ''}`}
            onClick={click}
            style={{cursor:'pointer', backgroundColor: 'white', position: 'relative'}}
            ref={cardStyleWithHover ? importantStyleRef(cardStyleWithHover) : undefined}
            onMouseEnter={props.cardHoverStyle ? () => setCardHovered(true) : undefined}
            onMouseLeave={props.cardHoverStyle ? () => setCardHovered(false) : undefined}
            // Drag-to-cart: when the add-to-cart affordance is available, the card is a drag source
            // carrying just the product identity; dropping it on the header cart icon adds it.
            draggable={props.showAddToCart && cartAddPermission}
            onDragStart={(props.showAddToCart && cartAddPermission) ? (e) => {
                e.dataTransfer.setData(CART_DRAG_MIME, JSON.stringify({
                    id: props.product.id,
                    catalog: props.product.catalog,
                    savedSearchId: props.product.savedSearchId,
                    bundleId: props.product.bundleId,
                    source: props.source,
                    searchContext: props.searchContext,
                }));
                e.dataTransfer.effectAllowed = 'copy';
            } : undefined}
        >
            {/* When a card background is configured, tint the image area to match the editor
                preview — otherwise the theme's `.card .fui-CardPreview { background: ... !important }`
                keeps it white/neutral. CardPreview forwards its ref to the DOM node, so we apply the
                color with inline `!important` (which outranks the theme rule). Defaults to white. */}
            {/* No onClick here: the parent Card's onClick already fires for clicks anywhere in the
                card (they bubble up). A second handler here double-fired nav(), pushing a duplicate
                history entry so Back appeared to do nothing on the first press. */}
            <CardPreview
                className={styles.grayBackground}
                ref={Object.keys(imageAreaStyle).length ? importantStyleRef(imageAreaStyle) : undefined}
                style={{cursor:'pointer', width: '100%', paddingTop: '10px', margin: '0 auto', backgroundColor: 'white'}}
            >
                <div className='pc-image-wrap' style={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'}}>
                    {tags.length > 0 && (
                        <div className='pc-tags-overlay'>
                            {tags.slice(0, 4).map((tag) => {
                                const c = tagColor(tag);
                                const clickable = !!props.onFilterTag;
                                const active = props.activeTags?.includes(tag) ?? false;
                                return (
                                    <span
                                        key={tag}
                                        className={`pc-tag-chip${clickable ? ' pc-tag-chip-clickable' : ''}${active ? ' pc-tag-chip-active' : ''}`}
                                        style={{ backgroundColor: c.background, color: c.color }}
                                        title={clickable ? (active ? `Remove ${tag} filter` : `Filter by tag: ${tag}`) : tag}
                                        onClick={clickable ? (e => { e.stopPropagation(); props.onFilterTag!(tag); }) : undefined}
                                    >
                                        <TagRegular className='pc-tag-icon' />{tag}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <ProductImage
                        className={styles.smallRadius}
                        src={primaryImage}
                        alt="Presentation Preview"
                        fit='contain'
                        style={{paddingLeft: '10px', paddingRight: '10px'}}
                    />
                    {hoverImage && (
                        <div className='pc-image-hover'>
                            <ProductImage
                                className={styles.smallRadius}
                                src={hoverImage}
                                alt=""
                                fit='contain'
                                style={{ maxWidth: '100%', maxHeight: '100%' }}
                            />
                        </div>
                    )}
                    {videoUrl && (
                        <Tooltip content="Play video" relationship="label">
                            <button
                                type="button"
                                aria-label="Play product video"
                                className='pc-video-play'
                                onClick={(e) => { e.stopPropagation(); setVideoOpen(true); }}
                            >
                                <PlayCircleFilled />
                            </button>
                        </Tooltip>
                    )}
                    {/* Configured product facet attributes, revealed on hover over the bottom of the
                        image so the card keeps its height. Cards are width-constrained, so cap the count
                        (the row/detail views carry the full set). */}
                    {props.product.attributes && props.product.attributes.length > 0 && (
                        <div className='pc-attrs-overlay'>
                            {props.product.attributes.slice(0, 4).map((attr, i) => {
                                const active = props.activeAttributes?.some(f => f.name === attr.key && f.value === attr.value) ?? false;
                                const clickable = !!props.onFilterAttribute;
                                return (
                                    <span
                                        className={`pc-attr-chip${clickable ? ' pc-attr-chip-clickable' : ''}${active ? ' pc-attr-chip-active' : ''}`}
                                        key={`${attr.key}-${i}`}
                                        title={clickable ? (active ? `Remove ${attr.key} filter` : `Filter by ${attr.key}: ${attr.value}`) : undefined}
                                        onClick={clickable ? (e => { e.stopPropagation(); props.onFilterAttribute!(attr.key, attr.value, props.product.category); }) : undefined}
                                    >
                                        <span className='pc-attr-key'>{attr.key}:</span> {attr.value}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            </CardPreview>

            {/* Top-edge filter shortcuts (Search "cards" view): apply this card's
                manufacturer / category as a search filter without leaving the page.
                stopPropagation keeps the card's navigate-to-product click from firing.
                Plain buttons (not Fluent) so the glyph inherits the card's luminance-aware
                text color — see .pc-filter-icon in index.css. */}
            {showsManufacturerFilter && (
                <button
                    type="button"
                    className="pc-filter-icon pc-filter-icon-left"
                    aria-label={`Filter by manufacturer ${props.product.manufacturer}`}
                    title={`Filter by manufacturer: ${props.product.manufacturer}`}
                    onClick={(e) => { e.stopPropagation(); props.onFilterManufacturer!(); }}
                >
                    <BuildingFactoryRegular />
                </button>
            )}
            {props.onFilterCategory && categoryLabel && (
                <button
                    type="button"
                    className="pc-filter-icon pc-filter-icon-right"
                    aria-label={`Filter by category ${categoryLabel}`}
                    title={`Filter by category: ${categoryLabel}`}
                    onClick={(e) => { e.stopPropagation(); props.onFilterCategory!(); }}
                >
                    <TagRegular />
                </button>
            )}

            {/* Dismiss control for the "Recommended For You" row: removes this card and records a
                negative signal so it isn't recommended again. Normally top-left, but the manufacturer
                filter icon claims that same corner, so shift right when both are showing rather than
                relying on the recommendation row never enabling manufacturer filtering. */}
            {onDismiss && (
                <Tooltip content={dismissLabel} relationship="label">
                    <button
                        type="button"
                        className={`pc-filter-icon pc-filter-icon-left${showsManufacturerFilter ? ' pc-filter-icon-left-shifted' : ''}`}
                        aria-label={dismissLabel}
                        title={dismissLabel}
                        onClick={(e) => { e.stopPropagation(); onDismiss(props.product.id); }}
                    >
                        <DismissRegular />
                    </button>
                </Tooltip>
            )}

            {/* Custom content layout (replaces Fluent CardHeader, which stacked the price
                directly under the title and let it fall off the fixed-height card when the
                title font was large). pc-content fills the space below the image as a flex
                column: the title is clamped to two lines and the price is pinned to the
                lower-left via margin-top:auto, so spacing stays consistent and the two never
                overlap regardless of the configured font sizes.
                The spans carry the merchant color with `!important` (the theme forces
                `.card .fui-Text/.fui-Caption1 { color: inherit !important }`); the Fluent
                element inherits it while its own inline style keeps the font/box styling. */}
            <div className='pc-content'>
                {/* Native tooltip with the full, untruncated name — so the title is still
                    readable when the card clips it to two lines. */}
                <span className='pc-title-wrap' title={props.product.name ?? ''} ref={props.nameStyle?.color ? importantStyleRef({color: props.nameStyle.color}) : undefined}>
                    <Text weight="semibold" className='pc-title' style={props.nameStyle}>
                        <TruncateText maxLength={50} text={props.product.name ?? ''} truncateOnWord />
                    </Text>
                </span>
                {/* One bottom block, not a price element plus loose siblings. The card height is fixed
                    per breakpoint and this block is pinned to the bottom with `margin-top: auto`, so
                    anything placed after it hangs off the card and gets clipped. Keeping the notices
                    inside means the block grows upward into the flexible middle instead. */}
                {/* Always rendered: the stock notice must survive even when the shopper cannot see
                    pricing at all, and an empty wrapper costs no height. */}
                <div className='pc-price-wrap' ref={props.priceStyle?.color ? importantStyleRef({color: props.priceStyle.color}) : undefined}>
                    {/* canSeePricing is belt-and-braces: the BFF already nulls Price for a shopper who
                        cannot see pricing. Checked anyway so the card matches the product page and the
                        search row layout rather than relying on the server alone. */}
                    {/* Above the price, because it qualifies WHICH price is being shown ("this is the
                        cheapest vendor's, for the first N") rather than commenting on the number after
                        the fact. Reads as a lead-in; underneath it read as an afterthought. */}
                    <PriceQuantityNotice
                        product={props.product}
                        canSeePricing={canSeePricing}
                        variant='short'
                        className='pc-price-ceiling'
                        style={{ margin: 0, fontSize: '0.75em', opacity: 0.8, lineHeight: 1.2 }}
                    />
                    {/* Price and promo pill share a line: the pill names what made the price what it
                        is, so it belongs beside it rather than under. Wraps if the pill is too long
                        for what the add-to-cart button leaves free. */}
                    <div className='pc-price-row'>
                        {(props.product.hidePrice || (props.product.price && canSeePricing)) && (
                            <Caption1 className={styles.caption} style={props.priceStyle}>
                                {props.product.hidePrice
                                    /* A saved-search facade has no concrete product to quote yet, so its placeholder stays text. */
                                    ? <QuotePrice
                                        itemId={props.product.bundleId ?? (props.product.savedSearchId ? undefined : props.product.id)}
                                        itemType={props.product.bundleId ? 'bundle' : 'product'}
                                        itemName={props.product.name}
                                    />
                                    : <CurrencyFormat value={props.product.price!} />}
                            </Caption1>
                        )}
                        <PromoPrice
                            product={props.product}
                            canSeePricing={canSeePricing}
                            part='badge'
                            className='pc-promo'
                            style={{ fontSize: '0.75em', lineHeight: 1.2 }}
                        />
                    </div>
                    {/* Directly under the price it replaced, so the two numbers line up vertically and
                        read as before/after rather than as two prices side by side. */}
                    <PromoPrice
                        product={props.product}
                        canSeePricing={canSeePricing}
                        part='was'
                        className='pc-promo-was'
                        style={{ fontSize: '0.75em', lineHeight: 1.2, paddingLeft: priceTextInset(props.priceStyle) }}
                    />
                    {/* Stock is worth saying regardless of whether pricing is visible. */}
                    <StockAvailabilityNotice
                        product={props.product}
                        className='pc-stock-low'
                        style={{ margin: 0, fontSize: '0.75em', opacity: 0.8, lineHeight: 1.2 }}
                    />
                </div>
            </div>
            {/* Favorite heart — sits beside the add-to-cart button, or takes its corner when the
                card has none. Renders nothing when favorites are off or the shopper is a guest. */}
            <FavoriteButton
                product={props.product}
                className={`sf-favorite-overlay${canAddToCart ? '' : ' sf-favorite-overlay-alone'}`}
                source={props.source}
                searchContext={props.searchContext}
            />
            {/* Floating Add to Cart Button */}
            {canAddToCart && (
                <Button
                    appearance="primary"
                    // Hook for the scoped :hover rule a referenced button style's `hover` part emits
                    // (see HomePage/SearchPage). Deliberately NOT `add-to-cart-button` — that class
                    // carries its own colors in index.css and would restyle this button.
                    className="sf-card-add-to-cart"
                    size="medium"
                    icon={isLoading ? <Spinner size="tiny" /> : <CartRegular />}
                    aria-label="Add to cart"
                    style={{
                        position: 'absolute',
                        right: 12,
                        bottom: 12,
                        zIndex: 2,
                        borderRadius: '50%',
                        minWidth: 0,
                        height: 30,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                        ...props.addToCartStyle,
                    }}
                    disabled={isLoading}
                    onClick={async (e) => {
                        e.stopPropagation();
                        // Read fresh values at click time, not from stale state
                        const activeCartId = settings.selectedCartId;
                        const usePreparedCart = !!activeCartId && (getPreparedCart.data?.canAddAdditionalItems ?? false);
                        let success = false;
                        setErrorMessage('');
                        try {
                            if (props.product.savedSearchId) {
                                if (usePreparedCart && props.product.id && props.product.catalog) {
                                    const result = await addToPreparedCart({ catalog: props.product.catalog, cartId: activeCartId!, productId: props.product.id });
                                    if (!checkError(result) && !checkMessages(result.data?.cart)) success = true;
                                } else {
                                    const result = await addSavedToCart({savedSearchId: props.product.savedSearchId, source: props.source, searchContext: props.searchContext});
                                    if (!checkError(result) && !checkMessages((result as any).data)) success = true;
                                }
                            } else if (props.product.bundleId) {
                                if (usePreparedCart) {
                                    const result = await addBundleToPreparedCart({cartId: activeCartId!, bundleId: props.product.bundleId});
                                    if (!checkError(result) && !checkMessages(result.data?.cart)) success = true;
                                } else {
                                    const result = await addBundleToCart({bundleId: props.product.bundleId, source: props.source, searchContext: props.searchContext});
                                    if (!checkError(result) && !checkMessages((result as any).data)) success = true;
                                }
                            } else {
                                if (usePreparedCart && props.product.id && props.product.catalog) {
                                    const result = await addToPreparedCart({ catalog: props.product.catalog, cartId: activeCartId!, productId: props.product.id });
                                    if (!checkError(result) && !checkMessages(result.data?.cart)) success = true;
                                } else {
                                    const result = await addToCart({productId: props.product.id, source: props.source, searchContext: props.searchContext});
                                    if (!checkError(result) && !checkMessages((result as any).data)) success = true;
                                }
                            }
                        } catch {}
                        if (success) {
                            setAdded(true);
                            setTimeout(() => setAdded(false), 1500);
                        }
                    }}
                />
            )}
            {/* Blocked Cart Button */}
            {cartBlocked && (
                <Tooltip content="Cannot add to current Prepared Cart" relationship="label">
                    <Button
                        appearance="subtle"
                        size="medium"
                        icon={<ProhibitedRegular style={{ color: '#d13438' }} />}
                        aria-label="Cannot add to cart"
                        style={{
                            position: 'absolute',
                            right: 12,
                            bottom: 12,
                            zIndex: 2,
                            borderRadius: '50%',
                            minWidth: 0,
                            height: 30,
                        }}
                        onClick={(e) => { e.stopPropagation(); setBlockedDialogOpen(true); }}
                    />
                </Tooltip>
            )}
            <Dialog open={blockedDialogOpen} onOpenChange={(_, d) => setBlockedDialogOpen(d.open)}>
                <DialogSurface onClick={e => e.stopPropagation()}>
                    <DialogBody>
                        <DialogTitle>Cannot Add to Cart</DialogTitle>
                        <DialogContent>
                            Your current Prepared Cart does not allow adding additional items.{canStartNewCart ? ' You can view your current cart or start a new one.' : ''}
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="primary" onClick={() => { setBlockedDialogOpen(false); nav(`/prepared-cart/${settings.selectedCartId}`); }}>
                                {L('localization_common_go_to_cart')}
                            </Button>
                            {canStartNewCart &&
                            <Button appearance="secondary" icon={<NewFilled/>} onClick={async () => { setBlockedDialogOpen(false); await startNewCart(); nav('/cart'); }}>
                                New Cart
                            </Button>}
                            <Button appearance="transparent" onClick={() => setBlockedDialogOpen(false)}>{L('localization_common_cancel')}</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            {videoUrl && (
                <ProductVideoModal
                    open={videoOpen}
                    onOpenChange={setVideoOpen}
                    videoUrl={videoUrl}
                    title={props.product.name ?? 'Product Video'}
                />
            )}
            {added && (
                <span
                    style={{
                        position: 'absolute',
                        right: 60,
                        bottom: 20,
                        background: 'rgba(255,255,255,0.95)',
                        color: '#00744d',
                        fontWeight: 600,
                        fontSize: 14,
                        borderRadius: 6,
                        padding: '2px 10px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        pointerEvents: 'none',
                        transition: 'opacity 0.2s',
                    }}
                >
                    {L('localization_common_added')}
                </span>
            )}
            {errorMessage && (
                <span
                    style={{
                        position: 'absolute',
                        left: 8,
                        right: 50,
                        bottom: 8,
                        background: 'rgba(255,255,255,0.97)',
                        color: '#d13438',
                        fontWeight: 600,
                        fontSize: 12,
                        borderRadius: 6,
                        padding: '4px 8px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                        pointerEvents: 'none',
                        zIndex: 3,
                    }}
                    onClick={e => { e.stopPropagation(); setErrorMessage(''); }}
                >
                    {errorMessage}
                </span>
            )}
        </Card>
    )
}

export default ProductCard;